// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — GIS Map
// Live map with zones, disposal sites, recent pickups, heat overlay,
// ERC district boundaries, TDSR sites, and 11 Tampa GIS layers
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Group, Paper, Badge, Stack, Text, Select, Loader, Center, Button,
} from '../../components/gab/GabComponents';
import { MapContainer, TileLayer, Marker, Popup, Polygon, Tooltip, CircleMarker, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import { useDebrisStore } from '../../store/debrisStore';
import { useIncidentZones } from '../../hooks/useIncidentZones';
import ZoneMapOverlay from '../../components/map/ZoneMapOverlay';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useAuthStore } from '../../store/authStore';
import { DEBRIS_TYPE_LABELS, DEBRIS_TYPE_COLORS, ERC_DISTRICTS } from '../../types/debris';
import type { DebrisType } from '../../types/debris';

// GeoJSON data — static imports for reasonably-sized files
import ercDistrictsData from '../../data/emergency-response-districts.json';
import tdsrSitesData from '../../data/tdsr-sites.json';
import responseCentersData from '../../data/response-centers.json';
import podsData from '../../data/points-of-distribution.json';
import fuelingSiteData from '../../data/fueling-site.json';
import pushRoutesErc1Data from '../../data/push-routes-erc-1.json';
import pushRoutesErc2Data from '../../data/push-routes-erc-2.json';
import pushRoutesErc3Data from '../../data/push-routes-erc-3.json';
import evacRoutesData from '../../data/evacuation-routes.json';
// NOTE: evacuation-zone.json (24.9MB) and fire-response-division-zone-daz.json are lazy-loaded

// Fix leaflet default icon
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
const DefaultIcon = L.icon({ iconUrl, shadowUrl: iconShadow, iconAnchor: [12, 41], popupAnchor: [1, -34] });
L.Marker.prototype.options.icon = DefaultIcon;

// Custom icons
function makeIcon(color: string) {
  return L.divIcon({
    className: '',
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>`,
  });
}

const ZONE_ICON = makeIcon('#16a34a');
const SITE_ICON = makeIcon('#B12525');
const PICKUP_ICON = makeIcon('#d97706');
const STOP_ICON = L.divIcon({
  className: '',
  iconSize: [10, 10],
  iconAnchor: [5, 5],
  html: `<div style="width:10px;height:10px;border-radius:50%;background:#f59e0b;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.3)"></div>`,
});

// TDSR site icon — larger diamond shape
function makeTDSRIcon(color: string) {
  return L.divIcon({
    className: '',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    html: `<div style="width:20px;height:20px;transform:rotate(45deg);background:${color};border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.5)"></div>`,
  });
}
const TDSR_ICON = makeTDSRIcon('#dc2626');

// ERC boundary colors
const ERC_COLORS: Record<string, string> = {
  '1': '#3B82F6',
  '2': '#10B981',
  '3': '#F59E0B',
};

// Leaflet.heat layer as a React component
function HeatLayer({ points }: { points: [number, number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const heat = (L as any).heatLayer(points, {
      radius: 35,
      blur: 25,
      maxZoom: 17,
      minOpacity: 0.3,
      max: 1.0,
      gradient: { 0.1: '#ffffb2', 0.25: '#fecc5c', 0.4: '#fd8d3c', 0.6: '#f03b20', 0.8: '#bd0026', 1.0: '#800026' },
    }).addTo(map);
    return () => { map.removeLayer(heat); };
  }, [points, map]);
  return null;
}

// Road Ownership — loads visible roads from Tampa ArcGIS on pan/zoom
function RoadOwnershipLayer({ show }: { show: boolean }) {
  const map = useMap();
  const layerRef = useRef<L.GeoJSON | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const OWNER_COLORS: Record<number, string> = {
    1: '#3B82F6',   // City — blue
    2: '#EF4444',   // State (FDOT) — red
    '-3': '#10B981', // County — green
    '-1': '#F59E0B', // Federal/Private — amber
  };
  const OWNER_NAMES: Record<string, string> = {
    '1': 'City of Tampa', '2': 'State (FDOT)', '-3': 'Hillsborough County', '-1': 'Federal/Private',
  };

  useEffect(() => {
    if (!show) {
      if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }
      return;
    }

    const geoJsonLayer = L.geoJSON(undefined, {
      style: (feature: any) => ({
        color: OWNER_COLORS[String(feature?.properties?.OWNEDBY)] || '#6b7280',
        weight: 4,
        opacity: 0.7,
      }),
      onEachFeature: (feature: any, layer: any) => {
        const p = feature?.properties || {};
        layer.bindPopup(
          `<b>${p.FULLNAME || 'Road'}</b><br/>` +
          `Owner: ${OWNER_NAMES[String(p.OWNEDBY)] || 'Unknown'}<br/>` +
          `Maintained by: ${OWNER_NAMES[String(p.MAINTBY)] || 'Unknown'}<br/>` +
          `Class: ${p.ROADCLASS || '-'}` +
          (p.DESIGNATION ? `<br/>Designation: ${p.DESIGNATION}` : '')
        );
      },
    }).addTo(map);
    layerRef.current = geoJsonLayer;

    const loadRoads = async () => {
      if (map.getZoom() < 12) return; // Don't load when zoomed out too far
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();

      const bounds = map.getBounds();
      const bbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;
      const baseUrl = 'https://arcgis.tampagov.net/arcgis/rest/services/OpenData/StreetsandAddresses/MapServer/2/query';
      const params = new URLSearchParams({
        where: "ROADCLASS <> 'Local' AND ROADCLASS IS NOT NULL AND ROADCLASS <> 'Vacated'",
        geometry: bbox,
        geometryType: 'esriGeometryEnvelope',
        inSR: '4326',
        spatialRel: 'esriSpatialRelIntersects',
        outFields: 'FULLNAME,OWNEDBY,MAINTBY,ROADCLASS,DESIGNATION',
        outSR: '4326',
        f: 'geojson',
        resultRecordCount: '2000',
      });

      try {
        const res = await fetch(`${baseUrl}?${params}`, { signal: abortRef.current.signal });
        if (!res.ok) return;
        const data = await res.json();
        if (data.features && layerRef.current) {
          layerRef.current.clearLayers();
          layerRef.current.addData(data);
        }
      } catch { /* aborted or failed */ }
    };

    loadRoads();
    map.on('moveend', loadRoads);

    return () => {
      map.off('moveend', loadRoads);
      if (abortRef.current) abortRef.current.abort();
      if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }
    };
  }, [show, map]);

  return null;
}

// Fit bounds only once on initial data load
function FitBoundsOnce({ bounds }: { bounds: L.LatLngBoundsExpression | null }) {
  const map = useMap();
  const hasFit = useRef(false);
  useEffect(() => {
    if (bounds && !hasFit.current) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
      hasFit.current = true;
    }
  }, [bounds, map]);
  return null;
}

export default function DebrisMap() {
  const navigate = useNavigate();
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const { zones, disposalSites, loadingZones, loadingSites, loadZones, loadDisposalSites } = useDebrisStore();
  const { activeZones: geoopsZones } = useIncidentZones();
  // Load ALL tickets for the map (not paginated store which only has 100)
  const [mapTickets, setMapTickets] = useState<any[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);

  // Existing toggles
  const [showZones, setShowZones] = useState(true);
  const [showSites, setShowSites] = useState(true);
  const [showPickups, setShowPickups] = useState(true);
  const [showHeat, setShowHeat] = useState(false);
  const [showERC, setShowERC] = useState(true);
  const [showTDSR, setShowTDSR] = useState(true);

  // New GIS layer toggles
  const [showPushRoutes1, setShowPushRoutes1] = useState(false);
  const [showPushRoutes2, setShowPushRoutes2] = useState(false);
  const [showPushRoutes3, setShowPushRoutes3] = useState(false);
  const [showEvacRoutes, setShowEvacRoutes] = useState(false);
  const [showResponseCenters, setShowResponseCenters] = useState(true);
  const [showPODS, setShowPODS] = useState(false);
  const [showFuelingSites, setShowFuelingSites] = useState(false);
  const [showEvacZones, setShowEvacZones] = useState(false);
  const [showFireDAZ, setShowFireDAZ] = useState(false);
  const [showRoadOwnership, setShowRoadOwnership] = useState(false);
  const [colorBy, setColorBy] = useState<'debris' | 'roadOwner'>('debris');
  const [roadOwnerFilter, setRoadOwnerFilter] = useState('all');
  const [layerPanelOpen, setLayerPanelOpen] = useState(true);

  // Lazy-loaded data for large files
  const [evacZoneData, setEvacZoneData] = useState<any>(null);
  const [fireDAZData, setFireDAZData] = useState<any>(null);
  const [loadingEvacZones, setLoadingEvacZones] = useState(false);
  const [loadingFireDAZ, setLoadingFireDAZ] = useState(false);

  // Lazy load evacuation zones when toggled on
  useEffect(() => {
    if (showEvacZones && !evacZoneData && !loadingEvacZones) {
      setLoadingEvacZones(true);
      import('../../data/evacuation-zone.json').then(data => {
        setEvacZoneData(data.default || data);
        setLoadingEvacZones(false);
      }).catch(() => setLoadingEvacZones(false));
    }
  }, [showEvacZones, evacZoneData, loadingEvacZones]);

  // Lazy load fire DAZ when toggled on
  useEffect(() => {
    if (showFireDAZ && !fireDAZData && !loadingFireDAZ) {
      setLoadingFireDAZ(true);
      import('../../data/fire-response-division-zone-daz.json').then(data => {
        setFireDAZData(data.default || data);
        setLoadingFireDAZ(false);
      }).catch(() => setLoadingFireDAZ(false));
    }
  }, [showFireDAZ, fireDAZData, loadingFireDAZ]);

  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [tileLayer, setTileLayer] = useState('street');

  useEffect(() => {
    loadZones();
    loadDisposalSites();
    // Load all tickets with GPS coordinates directly from API
    if (incidentId) {
      setLoadingTickets(true);
      (async () => {
        try {
          const { queryRecords } = await import('../../api/client');
          const { TABLES } = await import('../../api/tables');
          const { FIELDS } = await import('../../api/fields');
          const FT = FIELDS.debrisLoadTickets;
          const PAGE = 500;
          let offset = 0;
          let total = Infinity;
          const all: any[] = [];
          while (offset < total) {
            const res = await queryRecords<Record<string, unknown>>(TABLES.debrisLoadTickets, {
              limit: PAGE, offset,
              filters: [{ field: FT.incidentId, operator: 'eq' as const, value: incidentId }],
            });
            total = res.total;
            res.records.forEach((r: any) => {
              const lat = parseFloat(String(r[FT.pickupLat] || ''));
              const lng = parseFloat(String(r[FT.pickupLng] || ''));
              if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
                all.push({
                  id: r.record_id,
                  ticketNumber: String(r[FT.ticketNumber] || ''),
                  debrisType: String(r[FT.debrisType] || ''),
                  calculatedCubicYards: String(r[FT.calculatedCubicYards] || '0'),
                  pickupLat: String(lat),
                  pickupLng: String(lng),
                  status: String(r[FT.status] || ''),
                  subcontractorName: String(r[FT.subcontractorName] || ''),
                  disposalSiteName: String(r[FT.disposalSiteName] || ''),
                  roadName: String(r[FT.roadName] || ''),
                  roadOwnerName: String(r[FT.roadOwnerName] || ''),
                  roadOwner: String(r[FT.roadOwner] || ''),
                  roadClass: String(r[FT.roadClass] || ''),
                  truckNumber: String(r[FT.truckNumber] || ''),
                  clinCode: String(r[FT.clinCode] || ''),
                  extendedCost: String(r[FT.extendedCost] || ''),
                  transactionRate: String(r[FT.transactionRate] || ''),
                  loadDate: String(r[FT.loadDate] || ''),
                  contractorId: String(r[FT.contractorId] || ''),
                  truckId: String(r[FT.truckId] || ''),
                });
              }
            });
            offset += PAGE;
          }
          console.log(`[Map] Loaded ${all.length} tickets with GPS out of ${total} total`);
          setMapTickets(all);
        } catch (e) { console.error('[Map] Failed to load tickets:', e); }
        setLoadingTickets(false);
      })();
    }
  }, [incidentId, loadZones, loadDisposalSites]);

  const isLoading = loadingZones || loadingSites || loadingTickets;

  const iZones = incidentId ? zones : [];
  const iSites = incidentId ? disposalSites : [];
  const allTickets = mapTickets;
  const iTickets = useMemo(() => {
    let filtered = allTickets;
    // Debris type filter — always active
    if (typeFilter && typeFilter !== 'all') {
      filtered = filtered.filter(tk => tk.debrisType === typeFilter);
    }
    // Road owner filter — always active
    if (roadOwnerFilter && roadOwnerFilter !== 'all') {
      if (roadOwnerFilter === 'none') {
        filtered = filtered.filter(tk => !tk.roadOwnerName);
      } else {
        filtered = filtered.filter(tk => tk.roadOwnerName === roadOwnerFilter);
      }
    }
    return filtered;
  }, [allTickets, typeFilter, roadOwnerFilter]);

  // Compute bounds from ALL points (not affected by type filter so zoom is stable)
  const bounds = useMemo(() => {
    const points: [number, number][] = [];
    iZones.forEach(z => {
      const lat = parseFloat(z.centerLat); const lng = parseFloat(z.centerLng);
      if (lat && lng) points.push([lat, lng]);
    });
    iSites.forEach(s => {
      const lat = parseFloat(s.latitude); const lng = parseFloat(s.longitude);
      if (lat && lng) points.push([lat, lng]);
    });
    allTickets.forEach(t => {
      const lat = parseFloat(t.pickupLat); const lng = parseFloat(t.pickupLng);
      if (lat && lng) points.push([lat, lng]);
    });
    // Include TDSR sites in bounds calculation
    (tdsrSitesData as any).features.forEach((f: any) => {
      const [lng, lat] = f.geometry.coordinates;
      if (lat && lng) points.push([lat, lng]);
    });
    if (points.length === 0) return null;
    return L.latLngBounds(points);
  }, [iZones, iSites, allTickets]);

  // Parse zone boundaries
  const zoneBoundaries = useMemo(() =>
    iZones.map(z => {
      if (!z.boundaryGeojson) return null;
      try {
        const geo = JSON.parse(z.boundaryGeojson);
        if (geo.type === 'Polygon' && geo.coordinates?.[0]) {
          return {
            zone: z,
            positions: geo.coordinates[0].map((c: number[]) => [c[1], c[0]] as [number, number]),
          };
        }
      } catch { /* skip */ }
      return null;
    }).filter(Boolean) as Array<{ zone: typeof iZones[0]; positions: [number, number][] }>,
  [iZones]);

  // Heatmap data points: [lat, lng, intensity]
  const heatPoints = useMemo(() => {
    const raw: [number, number, number][] = [];
    for (const t of iTickets) {
      // Use individual stops for finer-grained heat
      try {
        const stops = JSON.parse(t.pickupStopsJson || '[]');
        if (Array.isArray(stops) && stops.length > 0) {
          for (const s of stops) {
            const lat = parseFloat(s.lat); const lng = parseFloat(s.lng);
            if (lat && lng) raw.push([lat, lng, parseFloat(s.unitCount || s.weight || '1') || 1]);
          }
          continue;
        }
      } catch { /* fall through */ }
      // Fallback to ticket centroid
      const lat = parseFloat(t.pickupLat); const lng = parseFloat(t.pickupLng);
      if (lat && lng) raw.push([lat, lng, parseFloat(t.calculatedCubicYards || '1') || 1]);
    }
    if (raw.length === 0) return [];
    // Normalize intensity to 0-1 so the gradient renders visibly
    const maxVal = Math.max(...raw.map(p => p[2]));
    return raw.map(([lat, lng, v]) => [lat, lng, maxVal > 0 ? Math.max(v / maxVal, 0.15) : 0.5] as [number, number, number]);
  }, [iTickets]);

  // Tampa center
  const defaultCenter: [number, number] = [27.95, -82.45];
  const DEBRIS_TYPE_SELECT = [{ value: 'all', label: 'All Types' }, ...Object.entries(DEBRIS_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }))];

  const roadOwnerCounts = useMemo(() => {
    const counts: Record<string, number> = { 'all': allTickets.length, 'none': 0 };
    allTickets.forEach(t => {
      const owner = t.roadOwnerName || '';
      if (!owner) counts['none'] = (counts['none'] || 0) + 1;
      else counts[owner] = (counts[owner] || 0) + 1;
    });
    return counts;
  }, [allTickets]);

  const ROAD_OWNER_SELECT = [
    { value: 'all', label: `All Road Owners (${roadOwnerCounts['all']?.toLocaleString()})` },
    ...(roadOwnerCounts['City of Tampa'] ? [{ value: 'City of Tampa', label: `City of Tampa (${roadOwnerCounts['City of Tampa']?.toLocaleString()})` }] : []),
    ...(roadOwnerCounts['State (FDOT)'] ? [{ value: 'State (FDOT)', label: `State / FDOT (${roadOwnerCounts['State (FDOT)']?.toLocaleString()})` }] : []),
    ...(roadOwnerCounts['Hillsborough County'] ? [{ value: 'Hillsborough County', label: `Hillsborough County (${roadOwnerCounts['Hillsborough County']?.toLocaleString()})` }] : []),
    ...(roadOwnerCounts['Federal/Private'] ? [{ value: 'Federal/Private', label: `Federal / Private (${roadOwnerCounts['Federal/Private']?.toLocaleString()})` }] : []),
    ...(roadOwnerCounts['none'] ? [{ value: 'none', label: `No Road Match (${roadOwnerCounts['none']?.toLocaleString()})` }] : []),
  ];

  const ROAD_OWNER_COLORS: Record<string, string> = {
    'City of Tampa': '#3B82F6',
    'State (FDOT)': '#EF4444',
    'Hillsborough County': '#10B981',
    'Federal/Private': '#F59E0B',
    '': '#9333EA', // No match — purple to stand out
  };

  const TILE_LAYERS: Record<string, { url: string; attribution: string }> = {
    street: { url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '&copy; OpenStreetMap' },
    satellite: { url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '&copy; Esri' },
    topo: { url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', attribution: '&copy; OpenTopoMap' },
    dark: { url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '&copy; CartoDB' },
  };
  const activeTile = TILE_LAYERS[tileLayer] ?? TILE_LAYERS.street;

  // ERC GeoJSON style — uses DISTRICT property from real data
  const ercStyle = (feature: any) => {
    const district = feature?.properties?.DISTRICT || feature?.properties?.district || '1';
    const color = ERC_COLORS[district] || '#6b7280';
    return {
      color,
      weight: 2,
      fillColor: color,
      fillOpacity: 0.12,
      dashArray: '6 4',
    };
  };

  // ERC GeoJSON onEachFeature
  const ercOnEach = (feature: any, layer: any) => {
    const p = feature?.properties;
    if (p) {
      const name = p.DEPARTMENT || p.name || `ERC ${p.DISTRICT || p.district}`;
      layer.bindTooltip(name, {
        permanent: true,
        direction: 'center',
        className: 'erc-label',
      });
    }
  };

  // Point layer helpers
  const responseCenterPointToLayer = (_feature: any, latlng: L.LatLng) =>
    L.circleMarker(latlng, { radius: 10, fillColor: '#7c3aed', color: '#fff', weight: 2, fillOpacity: 0.9 });
  const responseCenterOnEach = (feature: any, layer: any) =>
    layer.bindPopup(`<b>${feature.properties.FACNAME || 'Response Center'}</b>${feature.properties.ADDRESS ? '<br/>' + feature.properties.ADDRESS : ''}`);

  const podsPointToLayer = (_feature: any, latlng: L.LatLng) =>
    L.circleMarker(latlng, { radius: 7, fillColor: '#f97316', color: '#fff', weight: 2, fillOpacity: 0.9 });
  const podsOnEach = (feature: any, layer: any) =>
    layer.bindPopup(`<b>${feature.properties.FACNAME || 'POD'}</b>${feature.properties.ADDRESS ? '<br/>' + feature.properties.ADDRESS : ''}`);

  const fuelingPointToLayer = (_feature: any, latlng: L.LatLng) =>
    L.circleMarker(latlng, { radius: 6, fillColor: '#10B981', color: '#fff', weight: 2, fillOpacity: 0.9 });
  const fuelingOnEach = (feature: any, layer: any) =>
    layer.bindPopup(`<b>${feature.properties.FACNAME || 'Fueling Site'}</b>${feature.properties.ADDRESS ? '<br/>' + feature.properties.ADDRESS : ''}`);

  // Polygon styles
  const evacZoneStyle = { color: '#EF4444', weight: 1, fillColor: '#EF4444', fillOpacity: 0.15 };
  const fireDAZStyle = (feature: any) => ({
    color: '#6b7280', weight: 1.5, fillColor: '#9ca3af', fillOpacity: 0.15,
  });
  const fireDAZOnEach = (feature: any, layer: any) => {
    const p = feature?.properties;
    if (p?.LABEL || p?.DAZ) {
      layer.bindTooltip(p.LABEL || `DAZ ${p.DAZ}`, { direction: 'center', className: 'erc-label' });
    }
  };
  const evacZoneOnEach = (feature: any, layer: any) => {
    const p = feature?.properties;
    const label = p?.ZONE_NAME || p?.ZONE || p?.NAME || p?.LABEL || '';
    if (label) layer.bindTooltip(label, { direction: 'center' });
  };

  return (
    <Stack gap={8}>
      <div style={{ position: 'relative', zIndex: 10 }}>
        <Group justify="space-between" align="center" wrap="wrap">
          <Group gap="xs" align="center">
            <Title order={3} c="black">Debris Map</Title>
            {isLoading && <Loader size="xs" />}
            <Text size="xs" c="dimmed">{iTickets.length.toLocaleString()} tickets mapped</Text>
          </Group>
          <Group gap="xs">
            <Select data={[{ value: 'debris', label: 'Color: Debris Type' }, { value: 'roadOwner', label: 'Color: Road Owner' }]} value={colorBy} onChange={v => setColorBy((v as any) || 'debris')} size="xs" w={170} />
            <Select data={DEBRIS_TYPE_SELECT} value={typeFilter ?? 'all'} onChange={v => setTypeFilter(v)} size="xs" w={155} placeholder="Debris type" />
            <Select data={ROAD_OWNER_SELECT} value={roadOwnerFilter} onChange={v => setRoadOwnerFilter(v || 'all')} size="xs" w={195} placeholder="Road owner" />
          </Group>
        </Group>
      </div>

      <Paper withBorder style={{ height: 'calc(100vh - 200px)', minHeight: 500, borderRadius: 4, overflow: 'hidden', position: 'relative', zIndex: 0 }}>
        {/* ── Map Legend (inside map, bottom-left) ── */}
        <div style={{
          position: 'absolute', bottom: 10, left: 10, zIndex: 1000,
          background: 'rgba(255,255,255,0.95)', borderRadius: 8, padding: '10px 14px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)', maxWidth: 220, fontSize: '11px',
          maxHeight: 'calc(100% - 20px)', overflowY: 'auto',
        }}>
          {/* Ticket symbology */}
          {showPickups && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 700, marginBottom: 4, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Tickets — {colorBy === 'roadOwner' ? 'Road Owner' : 'Debris Type'}
              </div>
              {colorBy === 'roadOwner' ? (
                <div style={{ display: 'grid', gap: 3 }}>
                  {[
                    { color: '#3B82F6', label: 'City of Tampa' },
                    { color: '#EF4444', label: 'State (FDOT)' },
                    { color: '#10B981', label: 'Hillsborough County' },
                    { color: '#F59E0B', label: 'Federal / Private' },
                    { color: '#9333EA', label: 'No Road Match' },
                  ].map(r => (
                    <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: r.color, border: '1.5px solid #fff', boxShadow: '0 0 0 0.5px rgba(0,0,0,0.2)', flexShrink: 0 }} />
                      <span>{r.label}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 3 }}>
                  {Object.entries(DEBRIS_TYPE_LABELS).map(([key, label]) => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: DEBRIS_TYPE_COLORS[key as DebrisType] || '#d97706', border: '1.5px solid #fff', boxShadow: '0 0 0 0.5px rgba(0,0,0,0.2)', flexShrink: 0 }} />
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Infrastructure & Sites */}
          {(showZones || showSites || showTDSR || showResponseCenters || showPODS || showFuelingSites) && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 700, marginBottom: 4, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Markers</div>
              <div style={{ display: 'grid', gap: 3 }}>
                {showZones && <LegendDot color="#16a34a" label="Debris Zones" />}
                {showSites && <LegendDot color="#B12525" label="Disposal Sites" />}
                {showTDSR && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 10, height: 10, transform: 'rotate(45deg)', background: '#dc2626', border: '1.5px solid #fff', boxShadow: '0 0 0 0.5px rgba(0,0,0,0.2)', flexShrink: 0 }} />
                    <span>TDSR Sites</span>
                  </div>
                )}
                {showResponseCenters && <LegendDot color="#7c3aed" label="Response Centers" size={10} />}
                {showPODS && <LegendDot color="#f97316" label="Points of Distribution" size={8} />}
                {showFuelingSites && <LegendDot color="#10B981" label="Fueling Sites" size={8} />}
              </div>
            </div>
          )}

          {/* Lines & boundaries */}
          {(showERC || showPushRoutes1 || showPushRoutes2 || showPushRoutes3 || showEvacRoutes || showRoadOwnership || showEvacZones || showFireDAZ) && (
            <div style={{ marginBottom: showHeat ? 8 : 0 }}>
              <div style={{ fontWeight: 700, marginBottom: 4, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Boundaries & Routes</div>
              <div style={{ display: 'grid', gap: 3 }}>
                {showERC && ERC_DISTRICTS.map(d => (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 14, height: 10, borderRadius: 2, background: d.color, opacity: 0.3, border: `1.5px solid ${d.color}`, flexShrink: 0 }} />
                    <span>{d.name}</span>
                  </div>
                ))}
                {showPushRoutes1 && <LegendLine color="#3B82F6" label="Push Routes ERC 1" />}
                {showPushRoutes2 && <LegendLine color="#10B981" label="Push Routes ERC 2" />}
                {showPushRoutes3 && <LegendLine color="#F59E0B" label="Push Routes ERC 3" />}
                {showEvacRoutes && <LegendLine color="#EF4444" label="Evacuation Routes" dashed />}
                {showRoadOwnership && <>
                  <LegendLine color="#3B82F6" label="City Roads" thick />
                  <LegendLine color="#EF4444" label="State Roads" thick />
                  <LegendLine color="#10B981" label="County Roads" thick />
                </>}
                {showEvacZones && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 14, height: 10, borderRadius: 2, background: '#EF4444', opacity: 0.25, border: '1px solid #EF4444', flexShrink: 0 }} />
                    <span>Evacuation Zones</span>
                  </div>
                )}
                {showFireDAZ && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 14, height: 10, borderRadius: 2, background: '#9ca3af', opacity: 0.25, border: '1px solid #9ca3af', flexShrink: 0 }} />
                    <span>Fire Response DAZ</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {showHeat && (
            <div>
              <div style={{ fontWeight: 700, marginBottom: 4, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Heatmap</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 40, height: 8, borderRadius: 4, background: 'linear-gradient(to right, #ffffb2, #fecc5c, #fd8d3c, #f03b20, #800026)' }} />
                <span>CY density</span>
              </div>
            </div>
          )}
        </div>
        {/* ── Floating Layers Panel (top-right) ── */}
        <div style={{
          position: 'absolute', top: 10, right: 10, zIndex: 1000,
          background: 'rgba(255,255,255,0.97)', borderRadius: 8,
          boxShadow: '0 2px 12px rgba(0,0,0,0.18)', width: 224,
          fontSize: '11px', userSelect: 'none',
        }}>
          {/* Header */}
          <div
            onClick={() => setLayerPanelOpen(o => !o)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '7px 10px', cursor: 'pointer',
              borderBottom: layerPanelOpen ? '1px solid #e2e8f0' : 'none',
              borderRadius: layerPanelOpen ? '8px 8px 0 0' : 8,
            }}
          >
            <span style={{ fontWeight: 700, fontSize: '11px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Layers</span>
            <span style={{ fontSize: 12, color: '#64748b' }}>{layerPanelOpen ? '▲' : '▼'}</span>
          </div>

          {layerPanelOpen && (
            <div style={{ padding: '8px 10px 10px', maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' }}>
              {/* Basemap */}
              <div style={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>Basemap</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 10 }}>
                {(['street', 'satellite', 'topo', 'dark'] as const).map(t => (
                  <button key={t} onClick={() => setTileLayer(t)} style={{
                    padding: '3px 0', border: '1px solid', borderRadius: 4, cursor: 'pointer', fontSize: 11,
                    fontWeight: tileLayer === t ? 700 : 400,
                    background: tileLayer === t ? '#4B3FFF' : '#f8fafc',
                    color: tileLayer === t ? '#fff' : '#374151',
                    borderColor: tileLayer === t ? '#4B3FFF' : '#e2e8f0',
                  }}>
                    {t === 'street' ? 'Street' : t === 'satellite' ? 'Satellite' : t === 'topo' ? 'Terrain' : 'Dark'}
                  </button>
                ))}
              </div>

              {/* Operations */}
              <div style={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Operations</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
                <LayerToggle label="Push Routes ERC 1" checked={showPushRoutes1} onChange={setShowPushRoutes1} />
                <LayerToggle label="Push Routes ERC 2" checked={showPushRoutes2} onChange={setShowPushRoutes2} />
                <LayerToggle label="Push Routes ERC 3" checked={showPushRoutes3} onChange={setShowPushRoutes3} />
                <LayerToggle label="Evacuation Routes" checked={showEvacRoutes} onChange={setShowEvacRoutes} />
              </div>

              {/* Infrastructure */}
              <div style={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Infrastructure</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
                <LayerToggle label="Response Centers" checked={showResponseCenters} onChange={setShowResponseCenters} />
                <LayerToggle label="Points of Distribution" checked={showPODS} onChange={setShowPODS} />
                <LayerToggle label="Fueling Sites" checked={showFuelingSites} onChange={setShowFuelingSites} />
              </div>

              {/* Zones & Boundaries */}
              <div style={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Zones & Boundaries</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
                <LayerToggle label="ERC Boundaries" checked={showERC} onChange={setShowERC} />
                <LayerToggle label={loadingEvacZones ? 'Evac Zones (loading…)' : 'Evacuation Zones'} checked={showEvacZones} onChange={setShowEvacZones} />
                <LayerToggle label={loadingFireDAZ ? 'Fire DAZ (loading…)' : 'Fire Response DAZ'} checked={showFireDAZ} onChange={setShowFireDAZ} />
                <LayerToggle label="Road Ownership (live)" checked={showRoadOwnership} onChange={setShowRoadOwnership} />
              </div>

              {/* Debris Data */}
              <div style={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Debris Data</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <LayerToggle label="TDSR Sites" checked={showTDSR} onChange={setShowTDSR} />
                <LayerToggle label="Ticket Locations" checked={showPickups} onChange={setShowPickups} />
                <LayerToggle label="Zones" checked={showZones} onChange={setShowZones} />
                <LayerToggle label="Disposal Sites" checked={showSites} onChange={setShowSites} />
                <LayerToggle label="Heatmap" checked={showHeat} onChange={setShowHeat} />
              </div>
            </div>
          )}
        </div>

        {isLoading && iZones.length === 0 && iSites.length === 0 && allTickets.length === 0 && (
          <Center style={{ position: 'absolute', inset: 0, zIndex: 1000, background: 'rgba(255,255,255,0.8)' }}>
            <Stack align="center" gap="xs">
              <Loader size="lg" />
              <Text size="sm" c="dimmed">Loading map data...</Text>
            </Stack>
          </Center>
        )}

        {/* ERC label tooltip style */}
        <style>{`
          .erc-label {
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
            font-size: 11px;
            font-weight: 700;
            color: #374151;
            text-shadow: 1px 1px 2px rgba(255,255,255,0.9), -1px -1px 2px rgba(255,255,255,0.9);
          }
          .erc-label::before { display: none !important; }
        `}</style>

        <MapContainer center={defaultCenter} zoom={11} style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}>
          <TileLayer
            key={tileLayer}
            attribution={activeTile.attribution}
            url={activeTile.url}
          />
          <FitBoundsOnce bounds={bounds} />

          {/* ── Polygon layers (render first, under everything) ── */}

          {/* Evacuation Zones — lazy loaded */}
          {showEvacZones && evacZoneData && (
            <GeoJSON
              key="evac-zones"
              data={evacZoneData}
              style={evacZoneStyle}
              onEachFeature={evacZoneOnEach}
            />
          )}

          {/* Fire Response DAZ — lazy loaded */}
          {showFireDAZ && fireDAZData && (
            <GeoJSON
              key="fire-daz"
              data={fireDAZData}
              style={fireDAZStyle}
              onEachFeature={fireDAZOnEach}
            />
          )}

          {/* ERC District Boundaries — real data */}
          {showERC && (
            <GeoJSON
              key="erc-boundaries"
              data={ercDistrictsData as any}
              style={ercStyle}
              onEachFeature={ercOnEach}
            />
          )}

          {/* ── Polyline layers ── */}

          {/* Push Routes ERC 1 */}
          {showPushRoutes1 && (
            <GeoJSON
              key="push-routes-1"
              data={pushRoutesErc1Data as any}
              style={{ color: '#3B82F6', weight: 2, opacity: 0.7 }}
            />
          )}

          {/* Push Routes ERC 2 */}
          {showPushRoutes2 && (
            <GeoJSON
              key="push-routes-2"
              data={pushRoutesErc2Data as any}
              style={{ color: '#10B981', weight: 2, opacity: 0.7 }}
            />
          )}

          {/* Push Routes ERC 3 */}
          {showPushRoutes3 && (
            <GeoJSON
              key="push-routes-3"
              data={pushRoutesErc3Data as any}
              style={{ color: '#F59E0B', weight: 2, opacity: 0.7 }}
            />
          )}

          {/* Evacuation Routes */}
          {showEvacRoutes && (
            <GeoJSON
              key="evac-routes"
              data={evacRoutesData as any}
              style={{ color: '#EF4444', weight: 3, opacity: 0.8, dashArray: '8 6' }}
            />
          )}

          {/* Road ownership -- live from Tampa ArcGIS (renders under markers) */}
          <RoadOwnershipLayer show={showRoadOwnership} />

          {/* ── Point layers ── */}

          {/* Response Centers */}
          {showResponseCenters && (
            <GeoJSON
              key="response-centers"
              data={responseCentersData as any}
              pointToLayer={responseCenterPointToLayer}
              onEachFeature={responseCenterOnEach}
            />
          )}

          {/* Points of Distribution */}
          {showPODS && (
            <GeoJSON
              key="pods"
              data={podsData as any}
              pointToLayer={podsPointToLayer}
              onEachFeature={podsOnEach}
            />
          )}

          {/* Fueling Sites */}
          {showFuelingSites && (
            <GeoJSON
              key="fueling-sites"
              data={fuelingSiteData as any}
              pointToLayer={fuelingPointToLayer}
              onEachFeature={fuelingOnEach}
            />
          )}

          {/* TDSR Site Markers */}
          {showTDSR && (tdsrSitesData as any).features.map((f: any, idx: number) => {
            const [lng, lat] = f.geometry.coordinates;
            const p = f.properties;
            return (
              <Marker key={`tdsr-${idx}`} position={[lat, lng]} icon={TDSR_ICON}>
                <Popup>
                  <Stack gap={4} style={{ minWidth: 220 }}>
                    <Text fw={600} size="sm">{p.name}</Text>
                    <Group gap={6}>
                      <Badge size="xs" color="#dc2626">TDSR</Badge>
                      <Badge size="xs" variant="light">DEP: {p.depId}</Badge>
                    </Group>
                    <Text size="xs" c="dimmed">Accepted: {p.types}</Text>
                  </Stack>
                </Popup>
              </Marker>
            );
          })}

          {/* GeoOps incident zones */}
          <ZoneMapOverlay zones={geoopsZones} interactive />

          {/* Zone boundaries */}
          {showZones && zoneBoundaries.map(({ zone, positions }) => (
            <Polygon key={`zb-${zone.id}`} positions={positions}
              pathOptions={{ color: '#16a34a', weight: 2, fillOpacity: 0.15 }}>
              <Popup>
                <Stack gap={4} style={{ minWidth: 180 }}>
                  <Text fw={600} size="sm">{zone.zoneName}</Text>
                  <Badge size="xs" color={zone.status === 'active' ? 'var(--color-success)' : '#939598'}>{zone.status}</Badge>
                  <Text size="xs">Est: {parseFloat(zone.estimatedCubicYards || '0').toLocaleString()} CY</Text>
                  <Button size="compact-xs" variant="light" onClick={() => navigate('/debris/zones')}>View Zones</Button>
                </Stack>
              </Popup>
            </Polygon>
          ))}

          {/* Zone center markers */}
          {showZones && iZones.map(z => {
            const lat = parseFloat(z.centerLat); const lng = parseFloat(z.centerLng);
            if (!lat || !lng) return null;
            return (
              <Marker key={`z-${z.id}`} position={[lat, lng]} icon={ZONE_ICON}>
                <Popup>
                  <Stack gap={4} style={{ minWidth: 200 }}>
                    <Text fw={600} size="sm">{z.zoneName} ({z.zoneCode})</Text>
                    <Badge size="xs" color={z.status === 'active' ? 'var(--color-success)' : '#939598'}>{z.status}</Badge>
                    <Group gap="xs">
                      <Text size="xs">Est: {parseFloat(z.estimatedCubicYards || '0').toLocaleString()} CY</Text>
                      <Text size="xs" fw={600}>Actual: {parseFloat(z.actualCubicYards || '0').toLocaleString()} CY</Text>
                    </Group>
                    <Button size="compact-xs" variant="light" onClick={() => navigate('/debris/zones')}>View Zone Details</Button>
                  </Stack>
                </Popup>
              </Marker>
            );
          })}

          {/* Disposal sites */}
          {showSites && iSites.map(s => {
            const lat = parseFloat(s.latitude); const lng = parseFloat(s.longitude);
            if (!lat || !lng) return null;
            const cap = parseFloat(s.capacityCubicYards || '0');
            const vol = parseFloat(s.currentVolume || '0');
            const pct = cap > 0 ? ((vol / cap) * 100).toFixed(0) : '?';
            return (
              <Marker key={`s-${s.id}`} position={[lat, lng]} icon={SITE_ICON}>
                <Popup>
                  <Stack gap={4} style={{ minWidth: 220 }}>
                    <Text fw={600} size="sm">{s.siteName}</Text>
                    <Group gap={6}>
                      <Badge size="xs" tt="capitalize">{s.siteType}</Badge>
                      <Badge size="xs" color={s.status === 'open' ? 'var(--color-success)' : s.status === 'full' ? '#F86A0B' : '#939598'}>{s.status}</Badge>
                    </Group>
                    <Text size="xs">Fill: {pct}% ({vol.toLocaleString()} / {cap.toLocaleString()} CY)</Text>
                    {s.monitorName && <Text size="xs" c="dimmed">Monitor: {s.monitorName}</Text>}
                    <Button size="compact-xs" variant="light" onClick={() => navigate('/debris/sites')}>View Site Details</Button>
                  </Stack>
                </Popup>
              </Marker>
            );
          })}

          {/* Individual pickup stops from load tickets */}
          {showPickups && iTickets.flatMap(t => {
            const stops: any[] = (() => { try { return JSON.parse(t.pickupStopsJson || '[]'); } catch { return []; } })();
            if (stops.length > 0) {
              // Render individual stop markers
              return stops.map((s: any, idx: number) => {
                const sLat = parseFloat(s.lat); const sLng = parseFloat(s.lng);
                if (!sLat || !sLng) return null;
                return (
                  <Marker key={`stop-${t.id}-${idx}`} position={[sLat, sLng]} icon={STOP_ICON}>
                    <Popup>
                      <Stack gap={4} style={{ minWidth: 200 }}>
                        <Text fw={600} size="sm">{t.ticketNumber} -- Stop {idx + 1}</Text>
                        {s.debrisType && <Badge size="xs" variant="light">{DEBRIS_TYPE_LABELS[s.debrisType as DebrisType] ?? s.debrisType}</Badge>}
                        {s.address && <Text size="xs" c="dimmed">{s.address}</Text>}
                        {s.unitCount && <Text size="xs">{s.unitCount} {s.debrisType === 'vegetative' ? 'CY' : 'units'}</Text>}
                        <Badge size="xs" color={t.status === 'delivered' ? 'var(--color-success)' : t.status === 'voided' ? 'var(--color-error)' : '#F86A0B'}>{t.status?.replace(/_/g, ' ')}</Badge>
                      </Stack>
                    </Popup>
                  </Marker>
                );
              });
            }
            // Fallback: if no stops, use ticket-level centroid
            const lat = parseFloat(t.pickupLat); const lng = parseFloat(t.pickupLng);
            if (!lat || !lng) return [];
            const markerColor = colorBy === 'roadOwner'
              ? (ROAD_OWNER_COLORS[t.roadOwnerName] || ROAD_OWNER_COLORS[''])
              : (DEBRIS_TYPE_COLORS[t.debrisType as DebrisType] || '#d97706');
            return [(
              <CircleMarker key={`p-${t.id}-${colorBy}`} center={[lat, lng]}
                radius={6} fillColor={markerColor} color="#1e293b" weight={1.5} fillOpacity={0.9}>
                <Popup>
                  <Stack gap={4} style={{ minWidth: 240 }}>
                    <Group gap={6} justify="space-between">
                      <Text fw={700} size="sm">{t.ticketNumber}</Text>
                      <Badge size="xs" color={t.status === 'delivered' ? 'var(--color-success)' : t.status === 'voided' ? 'var(--color-error)' : '#F86A0B'}>{t.status?.replace(/_/g, ' ')}</Badge>
                    </Group>
                    <Group gap={6}>
                      <Badge size="xs" variant="light">{DEBRIS_TYPE_LABELS[t.debrisType as DebrisType] ?? t.debrisType}</Badge>
                      {t.clinCode && <Badge size="xs" variant="light" color="#4B3FFF">{t.clinCode}</Badge>}
                    </Group>
                    <div style={{ fontSize: '11px', lineHeight: 1.5 }}>
                      <div><b>CY:</b> {parseFloat(t.calculatedCubicYards || '0').toLocaleString()}</div>
                      {t.extendedCost && <div><b>Cost:</b> ${parseFloat(t.extendedCost).toLocaleString(undefined, { minimumFractionDigits: 2 })}{t.transactionRate ? ` @ $${parseFloat(t.transactionRate).toFixed(2)}/CY` : ''}</div>}
                      {t.truckNumber && <div><b>Truck:</b> {t.truckNumber}</div>}
                      {t.subcontractorName && <div><b>Sub:</b> {t.subcontractorName}</div>}
                      {t.loadDate && <div><b>Date:</b> {t.loadDate}</div>}
                      {t.disposalSiteName && <div><b>Disposal:</b> {t.disposalSiteName}</div>}
                      {t.roadName && <div><b>Road:</b> {t.roadName} — <span style={{ color: t.roadOwnerName === 'City of Tampa' ? '#3B82F6' : t.roadOwnerName?.includes('State') ? '#EF4444' : '#10B981' }}>{t.roadOwnerName}</span></div>}
                      {!t.roadName && <div style={{ color: '#D33423' }}><b>No road match</b></div>}
                    </div>
                    <Button size="compact-xs" variant="light" onClick={() => navigate(`/debris/ticket/${t.id}`)}>View Ticket 360</Button>
                  </Stack>
                </Popup>
              </CircleMarker>
            )];
          })}

          {/* Heat overlay -- real heatmap via leaflet.heat */}
          {showHeat && heatPoints.length > 0 && <HeatLayer points={heatPoints} />}
        </MapContainer>
      </Paper>
    </Stack>
  );
}

// ── Legend helper components ─────────────────────────────────
function LegendDot({ color, label, size = 10 }: { color: string; label: string; size?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: size, height: size, borderRadius: '50%', background: color, border: '1.5px solid #fff', boxShadow: '0 0 0 0.5px rgba(0,0,0,0.2)', flexShrink: 0 }} />
      <span>{label}</span>
    </div>
  );
}

function LegendLine({ color, label, dashed, thick }: { color: string; label: string; dashed?: boolean; thick?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{
        width: 18, height: 0, flexShrink: 0,
        borderTop: `${thick ? 4 : 2}px ${dashed ? 'dashed' : 'solid'} ${color}`,
      }} />
      <span>{label}</span>
    </div>
  );
}

function LayerToggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
      <div
        onClick={() => onChange(!checked)}
        style={{
          width: 28, height: 15, borderRadius: 8, flexShrink: 0,
          background: checked ? '#4B3FFF' : '#cbd5e1',
          position: 'relative', transition: 'background 0.15s',
        }}
      >
        <div style={{
          position: 'absolute', top: 2, left: checked ? 13 : 2,
          width: 11, height: 11, borderRadius: '50%', background: '#fff',
          transition: 'left 0.15s',
          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }} />
      </div>
      <span style={{ color: '#374151', fontSize: 11 }}>{label}</span>
    </label>
  );
}
